import { Box, Skeleton } from '@mui/material';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getSubImageDataUrl, type ImageMetadata } from '../../../components/getSubImageDataUrl';
import {
    FULL_FOV_WIDTH,
    VGA_WIDTH,
    VideoStreamType,
    geussStream,
    type streamLocation,
} from '../../../utils/ImageDimensions';

/**
 * In-cabin rectangle for wellness-style composites. Fixes {@link geussStream} returning the full
 * frame when stream layout is mis-detected (common with near-miss dimensions / CORS fallback).
 */
function inCabinCropForUniformGallery(nw: number, nh: number): streamLocation {
    const rect = geussStream(VideoStreamType.InCabinCamera, nw, nh);
    const [, , w, h] = rect;
    const looksLikeFullComposite = w >= nw - 2 && h >= nh - 2 && nw >= 900 && nh >= 360 && nh <= 600 && nw / nh >= 1.8;

    if (!looksLikeFullComposite) {
        return rect;
    }

    const nominalDualVga = 2 * VGA_WIDTH;
    const nominalVgaFov = VGA_WIDTH + FULL_FOV_WIDTH;
    if (Math.abs(nw - nominalDualVga) <= Math.abs(nw - nominalVgaFov) + 64) {
        return [0, 0, Math.floor(nw / 2), nh];
    }
    return [0, 0, Math.round((nw * VGA_WIDTH) / nominalVgaFov), nh];
}

interface PositionedCropProps {
    src: string;
    alt: string;
}

/**
 * Fallback when canvas export is blocked (CORS). Uses the same crop rect as {@link getSubImageDataUrl}
 * but positions the full image with plain DOM `style` so MUI RTL does not mirror `left`/`right` from `sx`.
 */
const PositionedCropFromStream: React.FC<PositionedCropProps> = ({ src, alt }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [natural, setNatural] = useState<{ nw: number; nh: number } | null>(null);
    const [cw, setCw] = useState(0);

    useEffect(() => {
        const img = new Image();
        img.src = src;
        img.onload = () => {
            if (img.naturalWidth && img.naturalHeight) {
                setNatural({ nw: img.naturalWidth, nh: img.naturalHeight });
            }
        };
        img.onerror = () => setNatural(null);
    }, [src]);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) {
            return undefined;
        }
        const ro = new ResizeObserver(() => setCw(el.clientWidth));
        ro.observe(el);
        setCw(el.clientWidth);
        return () => ro.disconnect();
    }, []);

    const region = natural ? inCabinCropForUniformGallery(natural.nw, natural.nh) : null;

    const scale = natural && region && cw > 0 ? cw / region[2] : 0;
    const offsetLeft = natural && region && cw > 0 ? -region[0] * scale : 0;
    const offsetTop = natural && region && cw > 0 ? -region[1] * scale : 0;

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: '100%',
                overflow: 'hidden',
                bgcolor: 'action.hover',
                aspectRatio: region ? `${region[2]} / ${region[3]}` : '4 / 3',
            }}
        >
            {natural && region && cw > 0 ? (
                <img
                    src={src}
                    alt={alt}
                    style={{
                        position: 'absolute',
                        left: offsetLeft,
                        top: offsetTop,
                        width: natural.nw * scale,
                        height: natural.nh * scale,
                        maxWidth: 'none',
                        display: 'block',
                    }}
                />
            ) : (
                <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
            )}
        </Box>
    );
};

export interface UniformInCabinGalleryThumbProps {
    src: string;
    alt: string;
}

/**
 * Thumbnail showing only the InCabin (left VGA) region. Uses {@link getSubImageDataUrl};
 * CORS fallback uses inline positioning (same crop math) so RTL does not swap sides.
 */
const UniformInCabinGalleryThumb: React.FC<UniformInCabinGalleryThumbProps> = ({ src, alt }) => {
    const [dataUrl, setDataUrl] = useState<string | null>(null);
    const [useFallback, setUseFallback] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setDataUrl(null);
        setUseFallback(false);

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const nw = img.naturalWidth;
            const nh = img.naturalHeight;
            if (!nw || !nh) {
                if (!cancelled) {
                    setUseFallback(true);
                }
                return;
            }
            const subImageData = inCabinCropForUniformGallery(nw, nh) as ImageMetadata;
            getSubImageDataUrl(src, subImageData)
                .then((url) => {
                    if (!cancelled) {
                        setDataUrl(url);
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setUseFallback(true);
                    }
                });
        };
        img.onerror = () => {
            if (!cancelled) {
                setUseFallback(true);
            }
        };
        img.src = src;

        return () => {
            cancelled = true;
        };
    }, [src]);

    if (useFallback) {
        return <PositionedCropFromStream src={src} alt={alt} />;
    }

    if (!dataUrl) {
        return (
            <Box sx={{ width: '100%', aspectRatio: '4 / 3' }}>
                <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
            </Box>
        );
    }

    return <img src={dataUrl} alt={alt} style={{ width: '100%', height: 'auto', display: 'block' }} />;
};

export default UniformInCabinGalleryThumb;
